import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { showToast } from "../helper/notifications";
import api from "../services/api";
import PageHeader from "../components/common/PageHeader";

const AuthorizeUserPage = () => {
  const { id } = useParams(); // Check if we are editing
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "editor",
  });

  // Fetch data only if in Edit Mode
  useEffect(() => {
    if (id) {
      const fetchUser = async () => {
        setFetching(true);
        try {
          const res = await api.get(`/users/${id}`);
          const userData = res.data?.user || res.data;
          setFormData({
            name: userData.name || "",
            email: userData.email || "",
            role: userData.role || "editor",
          });
        } catch (error) {
          showToast("error", "Failed to load user data");
          navigate("/users");
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
    }
  }, [id, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        // Edit existing user
        await api.put(`/users/${id}`, formData);
        showToast("success", "User updated successfully");
      } else {
        await api.post("/users", formData); 
        showToast("success", "Email authorized successfully");
      }
      navigate("/users");
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Operation failed";
      showToast("error", errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="d-flex justify-content-center py-80">
        <Icon icon="line-md:loading-twotone-loop" className="text-4xl text-primary-600" />
      </div>
    );
  }

  return (
    <>
      <PageHeader 
        title={id ? "Edit User Access" : "Authorize Email"} 
        subtitle="User Management" 
      />

      <div className="card radius-12 border-0 shadow-none bg-base">
        <div className="card-body p-24">
          <div className="mb-24">
            <h6 className="mb-4 text-lg fw-bold text-primary-light">
              {id ? "Update Permissions" : "New Access Authorization"}
            </h6>
            <p className="text-sm text-secondary-light">
              {id ? "Modify the name, email, or role for this user." : "Authorized users will be able to log in using this email address."}
            </p>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="row gy-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold text-secondary-light">Full Name</label>
                <input 
                  type="text" 
                  className="form-control radius-8" 
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required 
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold text-secondary-light">Email Address</label>
                <div className="icon-field">
                  <input 
                    type="email" 
                    className="form-control radius-8" 
                    placeholder="name@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required 
                  />
                  <span className="icon">
                    <Icon icon="mage:email" />
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold text-secondary-light">Assign Role</label>
                <select 
                  className="form-select radius-8"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  <option value="admin">Administrator</option>
                  <option value="editor">Editor</option>
                </select>
              </div>

              <div className="col-12 d-flex align-items-center justify-content-end gap-3 mt-24">
                <button 
                  type="button" 
                  className="btn bg-danger-focus text-danger-main px-24 py-11 radius-8 fw-semibold border-0"
                  onClick={() => navigate("/users")}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={loading}
                  className="btn btn-primary-600 px-24 py-11 radius-8 fw-semibold d-flex align-items-center gap-2"
                >
                  {loading ? <Icon icon="line-md:loading-twotone-loop" /> : <Icon icon="lucide:check-circle" />}
                  {id ? "Save Changes" : "Authorize Access"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default AuthorizeUserPage;